/**
 * A non-profit's numbers from the Donations desk (`npo_approvals`): the
 * registration under s.332 (formerly 12A/12AB), the approval under s.354
 * (formerly 80G), FCRA and NGO DARPAN. The org list reads the rows at sign-in
 * in the request that brings the business's name, and the Donations desk
 * hands back a fresh copy whenever it loads, so the switcher shows them at no
 * call of its own (regnos).
 *
 * Small on purpose: `useAuth` is in the first load and this is all it needs.
 *
 * @since regnos (2026-10-10)
 */

export type NpoNumberKey = 's332' | 's354' | 'fcra' | 'darpan';
export type NpoNumbers = Partial<Record<NpoNumberKey, string>>;

const KIND_TO_KEY: Record<string, NpoNumberKey> = {
    registration_332: 's332',
    approval_354: 's354',
    fcra_registration: 'fcra',
    darpan: 'darpan',
};

interface Row {
    kind?: string | null;
    approval_number?: string | null;
    status?: string | null;
    stage?: string | null;
    order_date?: string | null;
    covers_to_fy?: number | null;
}

/**
 * The live number of each kind. Several rows of one kind are a provisional
 * order and its regular one, or an old order and its renewal: the regular one
 * wins, then the latest order, then the one that runs longest. Returns
 * undefined when the rows were not read, so "not asked" never reads as "none".
 */
export function npoNumbersOf(rows: unknown): NpoNumbers | undefined {
    if (!Array.isArray(rows)) return undefined;
    const best: Partial<Record<NpoNumberKey, Row>> = {};
    for (const r of rows as Row[]) {
        const key = KIND_TO_KEY[r.kind ?? ''];
        const num = (r.approval_number ?? '').trim();
        if (!key || !num || (r.status ?? 'live') !== 'live') continue;
        const cur = best[key];
        if (!cur || ranks(r, cur) < 0) best[key] = r;
    }
    const out: NpoNumbers = {};
    for (const [k, r] of Object.entries(best) as Array<[NpoNumberKey, Row]>) {
        out[k] = (r.approval_number ?? '').trim().toUpperCase();
    }
    return out;
}

/** Negative when `a` should win over `b`. */
function ranks(a: Row, b: Row): number {
    const reg = (r: Row) => (r.stage === 'regular' ? 0 : 1);
    if (reg(a) !== reg(b)) return reg(a) - reg(b);
    const byDate = (b.order_date ?? '').localeCompare(a.order_date ?? '');
    if (byDate !== 0) return byDate;
    return (b.covers_to_fy ?? 0) - (a.covers_to_fy ?? 0);
}
